import React, { useState, useEffect } from 'react';
import { Download, Upload, Database, AlertTriangle, CheckCircle, Loader2, Building2, Phone, CreditCard, User, Landmark, Info, Copy, HelpCircle, Gift, Users, Share2, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function Settings() {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [importFile, setImportFile] = useState(null);
  
  // Company settings state
  const [companySettings, setCompanySettings] = useState({
    company_name: '',
    company_phone: '',
    pix_key: '',
    pix_holder: '',
    bank_name: '',
    additional_info: ''
  });
  const [savingCompany, setSavingCompany] = useState(false);
  const [companyMessage, setCompanyMessage] = useState(null);
  
  // Template variables
  const [variables, setVariables] = useState(null);
  
  // Referral data
  const [referralData, setReferralData] = useState(null);
  const [loadingReferral, setLoadingReferral] = useState(false);

  useEffect(() => {
    fetchCompanySettings();
    fetchTemplateVariables();
    fetchReferralData();
  }, []);

  const fetchReferralData = async () => {
    setLoadingReferral(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/auth/referral`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setReferralData(data);
      }
    } catch (error) {
      console.error('Error fetching referral data:', error);
    } finally {
      setLoadingReferral(false);
    }
  };

  const getReferralLink = () => {
    if (!referralData?.referral_code) return '';
    const baseUrl = window.location.origin;
    return `${baseUrl}/register?ref=${referralData.referral_code}`;
  };

  const copyReferralLink = () => {
    const link = getReferralLink();
    navigator.clipboard.writeText(link);
    toast.success('Link de indicação copiado!');
  };

  const shareReferralLink = () => {
    const link = getReferralLink();
    const text = `🎁 Use meu link para se cadastrar no MeuGestor e ganhe benefícios! ${link}`;
    
    if (navigator.share) {
      navigator.share({
        title: 'MeuGestor - Indicação',
        text: text,
        url: link
      });
    } else {
      navigator.clipboard.writeText(text);
      toast.success('Texto copiado para compartilhar!');
    }
  };

  const fetchCompanySettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/company-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCompanySettings(data);
      }
    } catch (error) {
      console.error('Error fetching company settings:', error);
    }
  };

  const fetchTemplateVariables = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/template-variables`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setVariables(data);
      }
    } catch (error) {
      console.error('Error fetching template variables:', error);
    }
  };

  const handleCompanyChange = (field, value) => {
    setCompanySettings(prev => ({ ...prev, [field]: value }));
  };

  const saveCompanySettings = async () => {
    setSavingCompany(true);
    setCompanyMessage(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/company-settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(companySettings)
      });
      
      if (!response.ok) {
        throw new Error('Erro ao salvar configurações');
      }
      
      setCompanyMessage({ type: 'success', text: 'Configurações da empresa salvas com sucesso!' });
    } catch (error) {
      setCompanyMessage({ type: 'error', text: error.message });
    } finally {
      setSavingCompany(false);
    }
  };

  const copyVariable = (variable) => {
    navigator.clipboard.writeText(variable);
    setCompanyMessage({ type: 'success', text: `Variável ${variable} copiada!` });
    setTimeout(() => setCompanyMessage(null), 2000);
  };

  const handleExport = async () => {
    setLoading(true);
    setMessage(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/data/export`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Erro ao exportar dados');
      }
      
      const data = await response.json();
      
      // Criar arquivo para download
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `whatsbot-backup-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      setMessage({
        type: 'success',
        text: `Exportação concluída! ${data.statistics.clients} clientes, ${data.statistics.groups} grupos, ${data.statistics.products} produtos, ${data.statistics.templates} templates.`
      });
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImportFile(file);
      setMessage(null);
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      setMessage({ type: 'error', text: 'Selecione um arquivo primeiro' });
      return;
    }
    
    setLoading(true);
    setMessage(null);
    
    try {
      const fileContent = await importFile.text();
      const importData = JSON.parse(fileContent);
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/data/import`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(importData)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erro ao importar dados');
      }
      
      const result = await response.json();
      
      setMessage({
        type: 'success',
        text: `Importação concluída! Clientes: ${result.results.clients.imported}, Grupos: ${result.results.groups.imported}, Produtos: ${result.results.products.imported}, Templates: ${result.results.templates.imported}`
      });
      
      setImportFile(null);
      // Limpar input file
      const fileInput = document.getElementById('import-file');
      if (fileInput) fileInput.value = '';
      
    } catch (error) {
      if (error instanceof SyntaxError) {
        setMessage({ type: 'error', text: 'Arquivo JSON inválido' });
      } else {
        setMessage({ type: 'error', text: error.message });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-2xl font-bold">Configurações</h1>
        <p className="text-gray-500">Gerencie seus dados e configurações do sistema</p>
      </div>

      <Tabs defaultValue="referral" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="referral" data-testid="tab-referral">
            <Gift className="h-4 w-4 mr-2" />
            Indicação
          </TabsTrigger>
          <TabsTrigger value="company" data-testid="tab-company">
            <Building2 className="h-4 w-4 mr-2" />
            Empresa
          </TabsTrigger>
          <TabsTrigger value="variables" data-testid="tab-variables">
            <HelpCircle className="h-4 w-4 mr-2" />
            Variáveis
          </TabsTrigger>
          <TabsTrigger value="backup" data-testid="tab-backup">
            <Database className="h-4 w-4 mr-2" />
            Backup
          </TabsTrigger>
        </TabsList>

        {/* Referral Tab */}
        <TabsContent value="referral" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Gift className="h-5 w-5 text-emerald-600" />
                Programa de Indicação
              </CardTitle>
              <CardDescription>
                Indique amigos e ganhe 30 dias de acesso para cada indicado que efetuar o primeiro pagamento!
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {loadingReferral ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                </div>
              ) : referralData ? (
                <>
                  {/* Link de Indicação */}
                  <div className="space-y-3">
                    <Label className="flex items-center gap-2 text-base font-semibold">
                      <Share2 className="h-4 w-4 text-violet-500" />
                      Seu Link de Indicação
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        readOnly
                        value={getReferralLink()}
                        className="bg-gray-50 font-mono text-sm"
                        data-testid="referral-link-input"
                      />
                      <Button 
                        variant="outline" 
                        onClick={copyReferralLink}
                        data-testid="copy-referral-link"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button 
                        onClick={shareReferralLink}
                        className="bg-emerald-600 hover:bg-emerald-700"
                        data-testid="share-referral-link"
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Compartilhar
                      </Button>
                    </div>
                    <p className="text-xs text-gray-500">
                      Seu código: <span className="font-mono font-bold text-violet-600">{referralData.referral_code}</span>
                    </p>
                  </div>

                  {/* Estatísticas */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-violet-50 rounded-lg p-4 text-center">
                      <Users className="h-6 w-6 mx-auto text-violet-600 mb-2" />
                      <p className="text-2xl font-bold text-violet-700">{referralData.total_referrals}</p>
                      <p className="text-xs text-violet-600">Total Indicados</p>
                    </div>
                    <div className="bg-emerald-50 rounded-lg p-4 text-center">
                      <CheckCircle className="h-6 w-6 mx-auto text-emerald-600 mb-2" />
                      <p className="text-2xl font-bold text-emerald-700">{referralData.paid_referrals}</p>
                      <p className="text-xs text-emerald-600">Pagamentos Confirmados</p>
                    </div>
                    <div className="bg-amber-50 rounded-lg p-4 text-center">
                      <Loader2 className="h-6 w-6 mx-auto text-amber-600 mb-2" />
                      <p className="text-2xl font-bold text-amber-700">{referralData.pending_referrals}</p>
                      <p className="text-xs text-amber-600">Aguardando Pagamento</p>
                    </div>
                    <div className="bg-sky-50 rounded-lg p-4 text-center">
                      <Gift className="h-6 w-6 mx-auto text-sky-600 mb-2" />
                      <p className="text-2xl font-bold text-sky-700">{referralData.total_bonus_days}</p>
                      <p className="text-xs text-sky-600">Dias Ganhos</p>
                    </div>
                  </div>

                  {/* Como funciona */}
                  <Alert className="border-emerald-500 bg-emerald-50">
                    <Gift className="h-4 w-4 text-emerald-600" />
                    <AlertDescription className="text-emerald-800 text-sm">
                      <strong>Como funciona:</strong>
                      <ol className="list-decimal list-inside mt-2 space-y-1">
                        <li>Compartilhe seu link de indicação</li>
                        <li>Seu amigo se cadastra usando o link</li>
                        <li>Quando ele fizer o primeiro pagamento (após o período de teste)</li>
                        <li>Você recebe automaticamente <strong>+30 dias</strong> de acesso!</li>
                      </ol>
                    </AlertDescription>
                  </Alert>

                  {/* Lista de indicados */}
                  {referralData.referred_users?.length > 0 && (
                    <div className="space-y-3">
                      <Label className="flex items-center gap-2 text-base font-semibold">
                        <Users className="h-4 w-4 text-violet-500" />
                        Seus Indicados
                      </Label>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {referralData.referred_users.map((user, index) => (
                          <div 
                            key={index}
                            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                          >
                            <div className="flex items-center gap-2">
                              <User className="h-4 w-4 text-gray-400" />
                              <span className="text-sm text-gray-700">{user.email}</span>
                            </div>
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              user.status === 'Bônus creditado'
                                ? 'bg-emerald-100 text-emerald-700'
                                : 'bg-amber-100 text-amber-700'
                            }`}>
                              {user.status}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <Alert className="border-red-500 bg-red-50">
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                  <AlertDescription className="text-red-800">
                    Erro ao carregar dados de indicação. Tente novamente mais tarde.
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Company Settings Tab */}
        <TabsContent value="company" className="space-y-4">
          {companyMessage && (
            <Alert className={companyMessage.type === 'success' ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'}>
              {companyMessage.type === 'success' ? (
                <CheckCircle className="h-4 w-4 text-green-600" />
              ) : (
                <AlertTriangle className="h-4 w-4 text-red-600" />
              )}
              <AlertDescription className={companyMessage.type === 'success' ? 'text-green-800' : 'text-red-800'}>
                {companyMessage.text}
              </AlertDescription>
            </Alert>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-blue-600" />
                Dados da Empresa
              </CardTitle>
              <CardDescription>
                Informações que serão usadas como variáveis nos templates de mensagem
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="company_name" className="flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    Nome da Empresa
                  </Label>
                  <Input
                    id="company_name"
                    data-testid="input-company-name"
                    placeholder="Minha Empresa LTDA"
                    value={companySettings.company_name}
                    onChange={(e) => handleCompanyChange('company_name', e.target.value)}
                  />
                  <p className="text-xs text-gray-500">Variável: {'{empresa}'} ou {'{nome_empresa}'}</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="company_phone" className="flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    Telefone da Empresa
                  </Label>
                  <Input
                    id="company_phone"
                    data-testid="input-company-phone"
                    placeholder="(11) 99999-9999"
                    value={companySettings.company_phone}
                    onChange={(e) => handleCompanyChange('company_phone', e.target.value)}
                  />
                  <p className="text-xs text-gray-500">Variável: {'{telefone_empresa}'}</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="pix_key" className="flex items-center gap-2">
                    <CreditCard className="h-4 w-4" />
                    Chave PIX
                  </Label>
                  <Input
                    id="pix_key"
                    data-testid="input-pix-key"
                    placeholder="email@empresa.com ou CPF/CNPJ"
                    value={companySettings.pix_key}
                    onChange={(e) => handleCompanyChange('pix_key', e.target.value)}
                  />
                  <p className="text-xs text-gray-500">Variável: {'{pix}'} ou {'{chave_pix}'}</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="pix_holder" className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Titular do PIX
                  </Label>
                  <Input
                    id="pix_holder"
                    data-testid="input-pix-holder"
                    placeholder="Nome do titular"
                    value={companySettings.pix_holder}
                    onChange={(e) => handleCompanyChange('pix_holder', e.target.value)}
                  />
                  <p className="text-xs text-gray-500">Variável: {'{titular}'} ou {'{titular_pix}'}</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="bank_name" className="flex items-center gap-2">
                    <Landmark className="h-4 w-4" />
                    Nome do Banco
                  </Label>
                  <Input
                    id="bank_name"
                    data-testid="input-bank-name"
                    placeholder="Banco do Brasil"
                    value={companySettings.bank_name}
                    onChange={(e) => handleCompanyChange('bank_name', e.target.value)}
                  />
                  <p className="text-xs text-gray-500">Variável: {'{banco}'} ou {'{nome_banco}'}</p>
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="additional_info" className="flex items-center gap-2">
                    <Info className="h-4 w-4" />
                    Informações Adicionais
                  </Label>
                  <Textarea
                    id="additional_info"
                    data-testid="input-additional-info"
                    placeholder="Horário de atendimento, endereço, etc."
                    value={companySettings.additional_info}
                    onChange={(e) => handleCompanyChange('additional_info', e.target.value)}
                    rows={3}
                  />
                  <p className="text-xs text-gray-500">Variável: {'{info_adicional}'}</p>
                </div>
              </div>

              <Button 
                onClick={saveCompanySettings} 
                disabled={savingCompany}
                className="w-full md:w-auto"
                data-testid="btn-save-company"
              >
                {savingCompany ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle className="h-4 w-4 mr-2" />
                )}
                Salvar Configurações
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Variables Reference Tab */}
        <TabsContent value="variables" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <HelpCircle className="h-5 w-5 text-purple-600" />
                Variáveis Disponíveis para Templates
              </CardTitle>
              <CardDescription>
                Clique em uma variável para copiar. Use essas variáveis nos seus templates de mensagem.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {variables ? (
                <div className="space-y-6">
                  {/* Client Variables */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                      <User className="h-5 w-5 text-blue-500" />
                      Variáveis do Cliente
                    </h3>
                    <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
                      {variables.client_variables.map((v, i) => (
                        <div 
                          key={i}
                          className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors border"
                          onClick={() => copyVariable(v.variable)}
                        >
                          <div className="flex items-center justify-between">
                            <code className="text-sm font-mono text-blue-600 bg-blue-50 px-2 py-1 rounded">
                              {v.variable}
                            </code>
                            <Copy className="h-4 w-4 text-gray-400" />
                          </div>
                          <p className="text-xs text-gray-600 mt-1">{v.description}</p>
                          <p className="text-xs text-gray-400 mt-1">Ex: {v.example}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Company Variables */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                      <Building2 className="h-5 w-5 text-green-500" />
                      Variáveis da Empresa
                    </h3>
                    <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
                      {variables.company_variables.map((v, i) => (
                        <div 
                          key={i}
                          className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors border"
                          onClick={() => copyVariable(v.variable)}
                        >
                          <div className="flex items-center justify-between">
                            <code className="text-sm font-mono text-green-600 bg-green-50 px-2 py-1 rounded">
                              {v.variable}
                            </code>
                            <Copy className="h-4 w-4 text-gray-400" />
                          </div>
                          <p className="text-xs text-gray-600 mt-1">{v.description}</p>
                          <p className="text-xs text-gray-400 mt-1">Ex: {v.example}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <Alert className="border-blue-500 bg-blue-50">
                    <Info className="h-4 w-4 text-blue-600" />
                    <AlertDescription className="text-blue-800 text-sm">
                      <strong>Dica:</strong> Use {'{valor}'} quando o template já tem "R$" (ex: "R$ {'{valor}'}" → "R$ 50.00"). 
                      Use {'{valor_completo}'} quando não tem (ex: "{'{valor_completo}'}" → "R$ 50.00").
                    </AlertDescription>
                  </Alert>
                </div>
              ) : (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Backup Tab */}
        <TabsContent value="backup" className="space-y-4">
          {message && (
            <Alert className={message.type === 'success' ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'}>
              {message.type === 'success' ? (
                <CheckCircle className="h-4 w-4 text-green-600" />
              ) : (
                <AlertTriangle className="h-4 w-4 text-red-600" />
              )}
              <AlertDescription className={message.type === 'success' ? 'text-green-800' : 'text-red-800'}>
                {message.text}
              </AlertDescription>
            </Alert>
          )}

          <div className="grid gap-6 md:grid-cols-2">
            {/* Export Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Download className="h-5 w-5 text-blue-600" />
                  Exportar Dados
                </CardTitle>
                <CardDescription>
                  Faça backup de todos os seus dados em formato JSON
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-sm text-gray-600 space-y-1">
                  <p>O arquivo exportado inclui:</p>
                  <ul className="list-disc list-inside ml-2">
                    <li>Todos os clientes</li>
                    <li>Grupos</li>
                    <li>Produtos</li>
                    <li>Templates de mensagens</li>
                    <li>Configurações de automação</li>
                  </ul>
                </div>
                <Button 
                  onClick={handleExport} 
                  disabled={loading}
                  className="w-full"
                  data-testid="btn-export"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4 mr-2" />
                  )}
                  Exportar Dados (JSON)
                </Button>
              </CardContent>
            </Card>

            {/* Import Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Upload className="h-5 w-5 text-green-600" />
                  Importar Dados
                </CardTitle>
                <CardDescription>
                  Restaure seus dados a partir de um backup
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Alert className="border-yellow-500 bg-yellow-50">
                  <AlertTriangle className="h-4 w-4 text-yellow-600" />
                  <AlertDescription className="text-yellow-800 text-sm">
                    A importação irá atualizar dados existentes com mesmo ID ou criar novos registros.
                  </AlertDescription>
                </Alert>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium">Selecionar arquivo</label>
                  <input
                    id="import-file"
                    type="file"
                    accept=".json"
                    onChange={handleFileSelect}
                    className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                  {importFile && (
                    <p className="text-sm text-green-600">
                      Arquivo selecionado: {importFile.name}
                    </p>
                  )}
                </div>
                
                <Button 
                  onClick={handleImport} 
                  disabled={loading || !importFile}
                  variant="outline"
                  className="w-full"
                  data-testid="btn-import"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4 mr-2" />
                  )}
                  Importar Dados
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Info Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5 text-purple-600" />
                Migração de Dados
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-sm text-gray-600 space-y-2">
                <p><strong>Para migrar seus dados para outro servidor:</strong></p>
                <ol className="list-decimal list-inside ml-2 space-y-1">
                  <li>Clique em "Exportar Dados" para baixar o arquivo JSON</li>
                  <li>Faça login no novo servidor</li>
                  <li>Vá em Configurações e clique em "Importar Dados"</li>
                  <li>Selecione o arquivo exportado</li>
                  <li>Aguarde a conclusão da importação</li>
                </ol>
                <p className="mt-4 text-yellow-700 bg-yellow-50 p-2 rounded">
                  <strong>Dica:</strong> Faça backups regularmente para não perder seus dados!
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default Settings;
